import useDeliveryServices from '@/hooks/useDeliveryServices';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getSearchResultsV31 } from '@/api/Search';
import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { getSuggestionsV31 } from '@/api/Search';
import { ProductCatalogueV2, Text, TextEnum, SnackBar, OffersSnackbar } from '@desquared/wave-components-library';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import useProductTags from '@/hooks/useProductTags';
import { getProductsWithFixedCategory } from '@/utils/category';
import { useRouter } from 'next/router';
import CategoryTitle from '@/components/CategoryTitle/CategoryTitle';
import styles from '../FullEditCatalogue.module.css';
import { useTranslations } from 'next-intl';
import { addProduct, removeProduct, openListSideSheet, getBundleOfferDescription } from '@/utils/products';
import { checkListStatus } from '@/utils/lists';
import { productsForOrder } from '@/store/cart';
import EmptyStateImg from '../../../../public/assets/wave-grocery/Empty State.png';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import useGetDefaultProductImage from '@/hooks/useGetDefaultProductImage';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';

export function SearchCatalogue({ query, categoriesFromFile, onSelect }) {
  const router = useRouter();
  const observer = useRef(null);
  const t = useTranslations();
  const a = useTranslations('common.asides');

  const { getCollectionTypes } = useDeliveryServices();
  const [collectionTypes, _] = useState(getCollectionTypes());
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [productsOrder, setProductsOrder] = useState([]);
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const defaultProductImage = useGetDefaultProductImage(0);
  const { personalizedProductsIds } = usePersonalizedProductsIds();

  const {
    data: searchResultsPages,
    isLoading: isLoadingCatalogueProducts,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['search-results', query],
    enabled: !!query,
    initialPageParam: 0,
    queryFn: ({ queryKey, pageParam }) =>
      getSearchResultsV31({
        term: query,
        collectionTypes: collectionTypes.length ? collectionTypes : ['Common'],
        pageNumber: pageParam.toString(),
        pageSize: 10,
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.pageNumber + 1 >= lastPage.totalPages) return undefined;

      return lastPage.pageNumber + 1;
    },
    staleTime: Infinity,
  });

  const { bundleOffersSummary } = useBundleOffersSummary();
  const { productTags } = useProductTags();

  const { data: suggestions, isLoading: isLoadingSuggestions } = useQuery({
    queryKey: ['search-suggestions', query],
    enabled: !!query,
    queryFn: ({ queryKey }) => getSuggestionsV31(queryKey[1], collectionTypes.length ? collectionTypes : ['Common']),
  });

  useEffect(() => {
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);
    return () => {
      productsForOrder$.unsubscribe();
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!suggestions) return;

    setSuggestedProducts(
      getProductsWithFixedCategory(
        suggestions.suggestedProducts,
        categoriesFromFile,
        productTags,
        bundleOffersSummary,
        router.locale,
        undefined,
        personalizedProductsIds,
        a
      )
    );
  }, [
    suggestions,
    bundleOffersSummary,
    categoriesFromFile,
    productTags,
    router.locale,
    productsOrder,
    personalizedProductsIds,
  ]);

  useEffect(() => {
    if (!searchResultsPages) return;
    setTotalCount(searchResultsPages.pages[0].totalCount);

    setSearchResults(
      getProductsWithFixedCategory(
        searchResultsPages.pages.flatMap(page => page.data?.products),
        categoriesFromFile,
        productTags,
        bundleOffersSummary,
        router.locale,
        undefined,
        personalizedProductsIds,
        a
      )
    );
  }, [
    searchResultsPages,
    bundleOffersSummary,
    categoriesFromFile,
    productTags,
    router.locale,
    productsOrder,
    personalizedProductsIds,
  ]);

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isLoadingCatalogueProducts || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [isLoadingCatalogueProducts, isFetchingNextPage, fetchNextPage]
  );
  return (
    <div className={styles.searchCatalogue}>
      <div>
        <CategoryTitle title={t('common.asides.full_edit_catalogue.search.title')} />
        <Text type={TextEnum.Assistive}>
          {t('common.asides.full_edit_catalogue.search.subtitle', { count: totalCount })}
        </Text>
      </div>

      {isLoadingSuggestions ? (
        <ProductCatalogueV2 isLoading products={[]} loaderTilesAmount={5} />
      ) : suggestedProducts.length ? (
        <div>
          <ProductCatalogueV2
            title={t('common.asides.search_product.suggested_title')}
            products={suggestedProducts}
            offerLabelHasRibbon={process.env.offerLabelHasRibbon}
            isTileDetailsAnimated={process.env.isTileDetailsAnimated}
            ctaIcon={process.env.ctaIcon}
            listIcon={process.env.listIcon}
            onListClick={openListSideSheet}
            checkStatus={checkListStatus}
            addProduct={product =>
              addProduct(
                product,
                false,
                {
                  item_list_id: 'searchCatalogue',
                  item_list_name: 'searchCatalogue',
                },
                false,
                t('common.asides.side_cart.no_comment')
              )
            }
            removeProduct={product =>
              removeProduct(
                product,
                {
                  item_list_id: 'searchCatalogue',
                  item_list_name: 'searchCatalogue',
                },
                t('common.asides.side_cart.no_comment')
              )
            }
            onProductDetails={onSelect}
            noItemsFoundImage={EmptyStateImg.src}
            noItemsFoundWording={t('common.empty_state_title')}
            setMaxSnackbar={setShowSnackbar}
            defaultProductImage={defaultProductImage}
            forYouLabel={t('common.user.profile.menu.for_you')}
          />
        </div>
      ) : null}

      {isLoadingCatalogueProducts ? (
        <ProductCatalogueV2 isLoading products={[]} loaderTilesAmount={5} />
      ) : (
        <>
          <div>
            <ProductCatalogueV2
              title={totalCount > 0 && t('common.asides.search_product.catalogue_products')}
              products={searchResults}
              offerLabelHasRibbon={process.env.offerLabelHasRibbon}
              isTileDetailsAnimated={process.env.isTileDetailsAnimated}
              ctaIcon={process.env.ctaIcon}
              listIcon={process.env.listIcon}
              lastItemRef={lastResultItemRef}
              onListClick={openListSideSheet}
              checkStatus={checkListStatus}
              addProduct={product =>
                addProduct(
                  product,
                  false,
                  {
                    item_list_id: 'searchCatalogue',
                    item_list_name: 'searchCatalogue',
                  },
                  false,
                  t('common.asides.side_cart.no_comment')
                )
              }
              removeProduct={product =>
                removeProduct(
                  product,
                  {
                    item_list_id: 'searchCatalogue',
                    item_list_name: 'searchCatalogue',
                  },
                  t('common.asides.side_cart.no_comment')
                )
              }
              onProductDetails={onSelect}
              noItemsFoundImage={EmptyStateImg.src}
              noItemsFoundWording={t('common.empty_state_title')}
              setMaxSnackbar={setShowSnackbar}
              defaultProductImage={defaultProductImage}
              forYouLabel={t('common.user.profile.menu.for_you')}
            />
          </div>

          {isFetchingNextPage ? <ProductCatalogueV2 isLoading products={[]} loaderTilesAmount={5} /> : null}
        </>
      )}
      <SnackBar open={showSnackBar} setOpen={setShowSnackbar} text={t('snackbar')} position={'center'} />
      {showBundleOffersSnackBar && (
        <OffersSnackbar
          emoji="🎉"
          open={showBundleOffersSnackBar}
          setOpen={BundleOfferSnackBarOpen.update}
          hasCloseOption={true}
          position="center"
          type="warning"
          autoWidth={true}
          buttonText={t('common.offers_snack_bar.btn_label')}
          text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
          onButtonPress={() => {
            setShowBundleOffersSnackBar(false);
            BundleOfferSnackBarOpen.update(false);
            BundleOfferSideSheetStore.update(true);
          }}
          autoHideDuration={8000}
          closeOnClickAway={true}
          hasShadow={true}
        />
      )}
    </div>
  );
}
